import React, {
    Fragment,
    FunctionComponent,
    useEffect,
    useState,
} from 'react';
import {
    Dropdown,
    DropdownButton,
    Row as BSRow,
    Table,
} from 'react-bootstrap';
import {
    Row,
    downloadSheets,
    generateSheet,
} from '../../../utils';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { AuthedLayout } from '../../../components';
import {
    StudentCountStatistic,
    UserInfo,
} from '../../../model';
import apis from '../../../apis';
import { toast } from 'react-toastify';

const borderStyle = {
    color: { rgb: '000000' },
    style: 'thin',
} as const;

const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;

const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (state: ApplicationState) => ({
    ...state.auth,
});

const mapDispatches = ErrorDispatches;

const connector = connect(
    mapState,
    mapDispatches
);

type Props =
    ConnectedProps<typeof connector>;

const otherStCount: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const {
        currentSemester = '',
        semesters = {},
    } = user || {};

    const [currentSem] =
        useState(currentSemester);

    const {
        year = -1,
        sem = -1,
    } = semesters[currentSem];

    const [datas, setDatas] =
        useState<StudentCountStatistic[]>([]);

    const [userinfo, setUser] =
        useState<UserInfo>();

    useEffect(() => {
        if (year && sem) {
            toast
                .promise(
                    apis
                        .getStudentCount(
                            year,
                            sem
                        )
                        .then((r) => {
                            setDatas(r);
                        }),
                    {
                        pending:
                            '資料載入中......',
                        success:
                            '資料載入成功！',
                        error:
                            '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(
                    catchErrorForModal
                );

            apis
                .getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(
                    catchErrorForModal
                );
        }
    }, [year, sem]);

    const gradeBase =
        datas.reduce(
            (
                grades,
                d
            ) => {
                return {
                    ...grades,
                    [d.grade]: [
                        ...(grades[
                            d.grade
                        ] || []),
                        d,
                    ],
                };
            },
            {} as Record<
                number,
                StudentCountStatistic[]
            >
        );

    let schooltotalcount = 0;
    let schoolmalecount = 0;
    let schoolfemalelcount = 0;

    const contentRows: Row[] =
        Object.values(
            gradeBase
        ).flatMap(
            (
                gs,
                idx,
                allArray
            ) => {
                const classCount = {
                    grade: 0,
                    classNo: 0,
                    totalCount: 0,
                    femaleCount: 0,
                    maleCount: 0,
                };

                const rows =
                    gs.flatMap(
                        (
                            g,
                            classIndex
                        ) => {
                            return StCountToRows(
                                g.grade,
                                g.className ??
                                '',
                                g.totalCount ??
                                0,
                                g.femaleCount ??
                                0,
                                g.maleCount ??
                                0,
                                classIndex ===
                                    0
                                    ? {
                                        row:
                                            gs.length +
                                            1,
                                    }
                                    : undefined
                            );
                        }
                    );

                gs.forEach(
                    (count) => {
                        classCount.totalCount +=
                            count.totalCount ??
                            0;

                        classCount.femaleCount +=
                            count.femaleCount ??
                            0;

                        classCount.maleCount +=
                            count.maleCount ??
                            0;

                        schooltotalcount +=
                            count.totalCount ??
                            0;

                        schoolfemalelcount +=
                            count.femaleCount ??
                            0;

                        schoolmalecount +=
                            count.maleCount ??
                            0;
                    }
                );

                rows.push(
                    ...StCountToRows(
                        '',
                        '小計',
                        classCount.totalCount,
                        classCount.femaleCount,
                        classCount.maleCount
                    )
                );

                if (
                    idx ===
                    allArray.length -
                    1
                ) {
                    const lastRow =
                        rows[
                        rows.length - 1
                        ];

                    lastRow.cells =
                        lastRow.cells.map(
                            (c) => {
                                const {
                                    style: {
                                        border = {},
                                    } = {},
                                } = c;

                                border.bottom =
                                    borderStyle;

                                c.style = {
                                    ...c.style,
                                    border,
                                };

                                return c;
                            }
                        );
                }

                return rows;
            }
        );

    const content: Row[] = [
        {
            cells: [
                {
                    value:
                        '年級/班級',
                    merge: {
                        column: 2,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '小計',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '男',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '女',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
            ],
        },
        ...contentRows,
        ...StCountToRows(
            '全校合計',
            '',
            schooltotalcount,
            schoolfemalelcount,
            schoolmalecount,
            {
                column: 2,
                row: 1,
            }
        ),
    ];

    const title =
        `${user.schools[user.currentSchool].name}` +
        `${year}學年${sem}學期_班級人數統計`;

    const handleExcelDownload = () => {
        const workSheet =
            generateSheet(
                [
                    {
                        cells: [
                            {
                                value:
                                    title,
                                style: {
                                    alignment: {
                                        horizontal:
                                            'left',
                                    },
                                },
                            },
                        ],
                    },
                    ...content,
                    {
                        cells: [
                            {
                                value:
                                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                style: {
                                    alignment: {
                                        horizontal:
                                            'left',
                                    },
                                },
                            },
                        ],
                    },
                ],
                {
                    alignment:
                        cellAlignStyle,
                }
            );

        downloadSheets(
            title,
            userinfo?.exportFileType,
            {
                sheet: workSheet,
                name: 'st',
            }
        );
    };

    const handlePrint = () => {
        const printWindow =
            window.open(
                '',
                '_blank',
                'width=1000,height=800'
            );

        if (!printWindow) {
            alert(
                '瀏覽器阻擋了列印視窗，請允許彈出式視窗。'
            );
            return;
        }

        const escapeHtml = (
            value: unknown
        ): string => {
            return String(
                value ?? ''
            )
                .replace(
                    /&/g,
                    '&amp;'
                )
                .replace(
                    /</g,
                    '&lt;'
                )
                .replace(
                    />/g,
                    '&gt;'
                )
                .replace(
                    /"/g,
                    '&quot;'
                )
                .replace(
                    /'/g,
                    '&#039;'
                );
        };

        const tableRows =
            content
                .map((row) => {
                    const cells =
                        row.cells
                            .map(
                                (
                                    cell
                                ) => {
                                    if (
                                        cell.value ===
                                        undefined
                                    ) {
                                        return '';
                                    }

                                    const colSpan =
                                        cell.merge
                                            ?.column
                                            ? ` colspan="${cell.merge.column}"`
                                            : '';

                                    const rowSpan =
                                        cell.merge
                                            ?.row
                                            ? ` rowspan="${cell.merge.row}"`
                                            : '';

                                    return `
                                        <td${colSpan}${rowSpan}>
                                            ${escapeHtml(
                                        cell.value
                                    )}
                                        </td>
                                    `;
                                }
                            )
                            .join('');

                    return `
                        <tr>
                            ${cells}
                        </tr>
                    `;
                })
                .join('');

        printWindow.document.open();

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="zh-Hant">
            <head>
                <meta charset="UTF-8" />
                <title>${escapeHtml(
            title
        )}</title>

                <style>
                    * {
                        box-sizing: border-box;
                    }

                    html,
                    body {
                        margin: 0;
                        padding: 0;
                        font-family:
                            "Microsoft JhengHei",
                            "Noto Sans TC",
                            Arial,
                            sans-serif;
                        color: #000;
                    }

                    .print-page {
                        min-height: 277mm;
                        display: flex;
                        flex-direction: column;
                    }

                    h1 {
                        margin: 0 0 15px;
                        text-align: center;
                        font-size: 18px;
                    }

                    table {
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: fixed;
                    }

                    td {
                        border: 1px solid #000;
                        padding: 6px 4px;
                        text-align: center;
                        vertical-align: middle;
                        font-size: 11px;
                    }

                    .footer {
                        margin-top: auto;
                        width: 100%;
                        padding-top: 20px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 12px;
                    }

                    @page {
                        size: A4 portrait;
                        margin: 10mm;
                    }

                    @media print {
                        body {
                            padding: 0;
                        }

                        tr {
                            page-break-inside:
                                avoid;
                            break-inside:
                                avoid;
                        }
                    }
                </style>
            </head>

            <body>
                <div class="print-page">

                    <h1>
                        ${escapeHtml(
            title
        )}
                    </h1>

                    <table>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>

                    <div class="footer">
                        <span>承辦人:</span>
                        <span>組長:</span>
                        <span>主任:</span>
                        <span>校長:</span>
                    </div>

                </div>

                <script>
                    window.onload =
                        function () {
                            window.focus();

                            setTimeout(
                                function () {
                                    window.print();
                                },
                                300
                            );
                        };

                    window.onafterprint =
                        function () {
                            window.close();
                        };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <BSRow className="justify-content-end">
                <DropdownButton
                    id="other-st-count-export"
                    title="匯出"
                    variant="success"
                    disabled={!datas.length}
                >
                    <Dropdown.Item
                        onClick={
                            handleExcelDownload
                        }
                    >
                        Excel 下載
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={
                            handlePrint
                        }
                    >
                        列印
                    </Dropdown.Item>
                </DropdownButton>
            </BSRow>

            <hr />

            <BSRow>
                <Table
                    striped
                    bordered
                    className="text-center"
                    style={{
                        tableLayout:
                            'fixed',
                    }}
                >
                    <tbody>
                        {content.map(
                            (
                                r,
                                i
                            ) => {
                                const cells =
                                    r.cells.map(
                                        (
                                            c,
                                            j
                                        ) =>
                                            c.value !==
                                                undefined ? (
                                                <td
                                                    colSpan={
                                                        c
                                                            .merge
                                                            ?.column
                                                    }
                                                    rowSpan={
                                                        c
                                                            .merge
                                                            ?.row
                                                    }
                                                    key={`${i}-${j}`}
                                                >
                                                    {
                                                        c.value
                                                    }
                                                </td>
                                            ) : (
                                                <Fragment
                                                    key={`${i}-${j}`}
                                                />
                                            )
                                    );

                                return (
                                    <tr key={i}>
                                        {cells}
                                    </tr>
                                );
                            }
                        )}
                    </tbody>
                </Table>
            </BSRow>
        </AuthedLayout>
    );
};

function StCountToRows(
    grade: number | string,
    classNo: number | string,
    totalCount: number,
    femaleCount: number,
    maleCount: number,
    gradeMerge?: {
        row?: number;
        column?: number;
    }
): Row[] {
    const gradeNo = [
        {
            value: gradeMerge
                ? grade
                : undefined,
            merge: gradeMerge,
            style: {
                border:
                    fullBorderStyle,
            },
        },
    ];

    if (!gradeMerge?.column) {
        gradeNo.push({
            value: classNo,
            merge: {
                row: 1,
            },
            style: {
                border:
                    fullBorderStyle,
            },
        });
    }

    return [
        {
            cells: [
                ...gradeNo,
                {
                    value:
                        totalCount ?? 0,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value:
                        maleCount ?? 0,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value:
                        femaleCount ?? 0,
                    style: {
                        border: {
                            right:
                                borderStyle,
                        },
                    },
                },
            ],
        },
    ];
}

export const OtherStCount =
    connector(otherStCount);